import { useId } from "react";
import {
  compactLiters,
  fillHeightFraction,
  percentFull,
  tankStatus,
} from "../lib/tankMath";

const DEFAULT_METRIC_OPTIONS = Object.freeze({
  showAxis: true,
  showCapacityLabel: true,
  showActualLabel: true,
  showZeroLabel: true,
  showRedZone: true,
  showMinimum: true,
  showLimitBands: true,
  showLimitLines: true,
});

export const TANK_METRIC_PRESETS = Object.freeze({
  detail: DEFAULT_METRIC_OPTIONS,
  limitsOnly: Object.freeze({
    showAxis: false,
    showActualLabel: false,
    showCapacityLabel: false,
    showZeroLabel: false,
    showRedZone: true,
    showMinimum: true,
    showLimitBands: true,
    showLimitLines: true,
  }),
  preview: Object.freeze({
    showAxis: false,
    showActualLabel: false,
    showCapacityLabel: false,
    showZeroLabel: false,
    showRedZone: true,
    showMinimum: true,
    showLimitBands: true,
    showLimitLines: true,
  }),
});

const VARIANT_METRIC_OPTIONS = Object.freeze({
  large: Object.freeze({}),
  mini: TANK_METRIC_PRESETS.preview,
});

function resolveMetricOptions(variant, metrics) {
  return {
    ...DEFAULT_METRIC_OPTIONS,
    ...(VARIANT_METRIC_OPTIONS[variant] ?? null),
    ...(metrics ?? null),
  };
}

function normalizeTank({
  capacityLiters,
  id,
  minDispensableLiters = 0,
  product,
  redZoneLiters = 0,
  status = "normal",
  type = "vertical-open",
  volumeLiters,
  warningLiters = 0,
}) {
  return {
    capacityLiters,
    id,
    minDispensableLiters,
    product,
    redZoneLiters,
    status,
    type,
    volumeLiters,
    warningLiters,
  };
}

export function TankVisualization({
  ariaLabel,
  capacityLiters,
  className = "",
  id,
  metrics,
  minDispensableLiters,
  product,
  redZoneLiters,
  status,
  type,
  variant = "large",
  volumeLiters,
  warningLiters,
}) {
  const tank = normalizeTank({
    capacityLiters,
    id,
    minDispensableLiters,
    product,
    redZoneLiters,
    status,
    type,
    volumeLiters,
    warningLiters,
  });
  const metricOptions = resolveMetricOptions(variant, metrics);
  const commonProps = {
    ariaLabel: ariaLabel ?? `${tank.id} ${tank.product} level`,
    className,
    compact: variant === "mini",
    metrics: metricOptions,
    status: tankStatus(tank),
    tank,
  };

  if (tank.type === "horizontal-cylinder") {
    return <HorizontalTank {...commonProps} />;
  }

  return <VerticalTank {...commonProps} />;
}

function VerticalTank({ ariaLabel, className, compact, metrics, status, tank }) {
  const reactId = useId().replaceAll(":", "");
  const clipId = `clip-v-${tank.id}-${reactId}`;
  const gradientId = `fluid-v-${tank.id}-${reactId}`;
  const shellId = `shell-v-${tank.id}-${reactId}`;
  const roofId = `roof-v-${tank.id}-${reactId}`;
  const glassId = `glass-v-${tank.id}-${reactId}`;
  const fill = fillHeightFraction(tank);
  const redFill = fillHeightFraction(tank, tank.redZoneLiters);
  const minFill = fillHeightFraction(tank, tank.minDispensableLiters);
  const inner = { x: 128, y: 104, width: 174, height: 286 };
  const bottom = inner.y + inner.height;
  const fillY = bottom - inner.height * fill;
  const redY = bottom - inner.height * redFill;
  const minY = bottom - inner.height * minFill;
  const waveY = fillY;

  return (
    <svg
      className={`tank-svg vertical ${compact ? "compact" : ""} status-${status} ${className}`.trim()}
      viewBox={compact ? "82 18 282 414" : "0 0 430 458"}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="var(--fluid-dark)" />
          <stop offset="48%" stopColor="var(--fluid)" />
          <stop offset="100%" stopColor="var(--fluid-light)" />
        </linearGradient>
        <linearGradient id={shellId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#eef4f6" />
          <stop offset="16%" stopColor="#ffffff" />
          <stop offset="38%" stopColor="#cbd7dc" />
          <stop offset="58%" stopColor="#f8fbfc" />
          <stop offset="78%" stopColor="#d4dee2" />
          <stop offset="100%" stopColor="#f6fafb" />
        </linearGradient>
        <linearGradient id={roofId} x1="0%" x2="0%" y1="0%" y2="100%">
          <stop offset="0%" stopColor="#ffffff" />
          <stop offset="100%" stopColor="#d2dde1" />
        </linearGradient>
        <linearGradient id={glassId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.34)" />
          <stop offset="18%" stopColor="rgba(255,255,255,0.08)" />
          <stop offset="50%" stopColor="rgba(255,255,255,0.22)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.05)" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect x={inner.x} y={inner.y} width={inner.width} height={inner.height} rx="4" />
        </clipPath>
      </defs>

      {metrics.showAxis ? <VerticalAxis tank={tank} x={42} inner={inner} minY={minY} redY={redY} fillY={fillY} metrics={metrics} /> : null}

      <path className="tank-shadow" d="M104 407h226l35 12H69z" />
      <path className="tank-shell" d="M116 103h199v291H116z" fill={`url(#${shellId})`} />
      <ellipse className="tank-top" cx="215.5" cy="103" rx="99.5" ry="25" fill={`url(#${roofId})`} />
      <path className="tank-roof" d="M122 104c13-47 54-75 94-75s81 28 94 75z" fill={`url(#${roofId})`} />
      <path className="tank-roof-ribs" d="M145 100c10-31 38-52 70-52s60 21 70 52M173 101c6-26 22-44 43-52M257 101c-6-26-22-44-42-52" />
      <rect className="tank-cap" x="188" y="21" width="56" height="14" rx="3" />
      <rect className="tank-cap-line" x="181" y="34" width="70" height="8" rx="3" />
      <path className="tank-panel-lines" d="M146 122v258M181 116v268M216 114v272M251 116v268M286 122v258M122 149h187M122 194h187M122 239h187M122 284h187M122 329h187" />

      <g clipPath={`url(#${clipId})`}>
        <rect className="fluid-fill" x={inner.x} y={fillY} width={inner.width} height={bottom - fillY} fill={`url(#${gradientId})`} />
        <path
          className="fluid-wave"
          d={`M${inner.x} ${waveY} C ${inner.x + 30} ${waveY - 7}, ${inner.x + 52} ${waveY + 5}, ${
            inner.x + 82
          } ${waveY} S ${inner.x + 139} ${waveY - 5}, ${inner.x + inner.width} ${waveY} L ${
            inner.x + inner.width
          } ${bottom} L ${inner.x} ${bottom} Z`}
          fill={`url(#${gradientId})`}
        />
        {metrics.showRedZone && metrics.showLimitBands ? <rect className="red-zone-fill" x={inner.x} y={redY} width={inner.width} height={bottom - redY} /> : null}
        <rect className="glass-stripe" x={inner.x + 33} y={inner.y} width="22" height={inner.height} />
        <rect className="glass-stripe soft" x={inner.x + 98} y={inner.y} width="26" height={inner.height} />
        <rect className="tank-glass-overlay" x={inner.x} y={inner.y} width={inner.width} height={inner.height} fill={`url(#${glassId})`} />
      </g>

      {metrics.showMinimum && metrics.showLimitLines ? <line className="min-line" x1={inner.x - 6} y1={minY} x2={inner.x + inner.width + 6} y2={minY} /> : null}
      {metrics.showRedZone && metrics.showLimitLines ? <line className="red-line" x1={inner.x - 6} y1={redY} x2={inner.x + inner.width + 6} y2={redY} /> : null}
      <rect className="tank-outline" x="116" y="103" width="199" height="291" />
      <ellipse className="tank-top-outline" cx="215.5" cy="103" rx="99.5" ry="25" />

      <path className="tank-pipe" d="M323 86h22c12 0 21 9 21 21v265c0 14 8 24 22 24" />
      <path className="tank-pipe secondary" d="M331 355h27c8 0 14 6 14 14v21" />
      <path className="tank-ladder" d="M340 77v310M330 94h20M330 127h20M330 160h20M330 193h20M330 226h20M330 259h20M330 292h20M330 325h20M330 358h20" />
      <path className="tank-base" d="M104 390h224v19H104zM83 409h266v12H83z" />
      <path className="tank-base-bolts" d="M121 401h0M151 401h0M181 401h0M211 401h0M241 401h0M271 401h0M301 401h0" />
    </svg>
  );
}

function HorizontalTank({ ariaLabel, className, compact, metrics, status, tank }) {
  const reactId = useId().replaceAll(":", "");
  const clipId = `clip-h-${tank.id}-${reactId}`;
  const gradientId = `fluid-h-${tank.id}-${reactId}`;
  const shellId = `shell-h-${tank.id}-${reactId}`;
  const glassId = `glass-h-${tank.id}-${reactId}`;
  const fill = fillHeightFraction(tank);
  const redFill = fillHeightFraction(tank, tank.redZoneLiters);
  const minFill = fillHeightFraction(tank, tank.minDispensableLiters);
  const body = { x: 58, y: 72, width: 312, height: 124, rx: 62 };
  const leftCapX = body.x + body.rx;
  const rightCapX = body.x + body.width - body.rx;
  const centerY = body.y + body.height / 2;
  const bodyPath = `M${leftCapX} ${body.y} H${rightCapX} C${rightCapX + 34} ${body.y}, ${body.x + body.width} ${
    centerY - 34
  }, ${body.x + body.width} ${centerY} C${body.x + body.width} ${centerY + 34}, ${rightCapX + 34} ${
    body.y + body.height
  }, ${rightCapX} ${body.y + body.height} H${leftCapX} C${leftCapX - 34} ${body.y + body.height}, ${body.x} ${
    centerY + 34
  }, ${body.x} ${centerY} C${body.x} ${centerY - 34}, ${leftCapX - 34} ${body.y}, ${leftCapX} ${body.y} Z`;
  const bottom = body.y + body.height;
  const fillY = bottom - body.height * fill;
  const redY = bottom - body.height * redFill;
  const minY = bottom - body.height * minFill;

  return (
    <svg
      className={`tank-svg horizontal ${compact ? "compact" : ""} status-${status} ${className}`.trim()}
      viewBox={compact ? "42 42 364 190" : "0 0 430 258"}
      role="img"
      aria-label={ariaLabel}
    >
      <defs>
        <linearGradient id={gradientId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="var(--fluid-dark)" />
          <stop offset="52%" stopColor="var(--fluid)" />
          <stop offset="100%" stopColor="var(--fluid-light)" />
        </linearGradient>
        <linearGradient id={shellId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="#e7eff2" />
          <stop offset="18%" stopColor="#ffffff" />
          <stop offset="43%" stopColor="#c8d4d9" />
          <stop offset="67%" stopColor="#fbfdfe" />
          <stop offset="100%" stopColor="#d4dee2" />
        </linearGradient>
        <linearGradient id={glassId} x1="0%" x2="100%" y1="0%" y2="0%">
          <stop offset="0%" stopColor="rgba(255,255,255,0.2)" />
          <stop offset="48%" stopColor="rgba(255,255,255,0.36)" />
          <stop offset="100%" stopColor="rgba(255,255,255,0.06)" />
        </linearGradient>
        <clipPath id={clipId}>
          <path d={bodyPath} />
        </clipPath>
      </defs>

      {metrics.showAxis ? <HorizontalAxis tank={tank} x={35} body={body} minY={minY} redY={redY} fillY={fillY} metrics={metrics} /> : null}

      <path className="tank-shadow" d="M71 214h303l30 12H42z" />
      <g clipPath={`url(#${clipId})`}>
        <path className="tank-shell-bg" d={bodyPath} fill={`url(#${shellId})`} />
        <rect className="fluid-fill" x={body.x} y={fillY} width={body.width} height={bottom - fillY} fill={`url(#${gradientId})`} />
        {metrics.showRedZone && metrics.showLimitBands ? <rect className="red-zone-fill" x={body.x} y={redY} width={body.width} height={bottom - redY} /> : null}
        <rect className="glass-stripe" x={body.x + 78} y={body.y} width="34" height={body.height} />
        <rect className="glass-stripe soft" x={body.x + 188} y={body.y} width="40" height={body.height} />
        <rect className="tank-glass-overlay" x={body.x} y={body.y} width={body.width} height={body.height} fill={`url(#${glassId})`} />
        <path
          className="tank-panel-lines horizontal-lines"
          d={`M88 104 C142 96, 286 96, 340 104
              M76 134 C136 128, 294 128, 354 134
              M88 166 C142 174, 286 174, 340 166
              M${leftCapX} ${body.y + 2} C100 90, 91 110, 91 ${centerY} C91 158, 101 180, ${leftCapX} ${
                bottom - 2
              }
              M${rightCapX} ${body.y + 2} C330 90, 339 110, 339 ${centerY} C339 158, 329 180, ${rightCapX} ${
                bottom - 2
              }
              M184 74 C178 104, 178 164, 184 194
              M246 74 C252 104, 252 164, 246 194`}
        />
      </g>
      <path className="tank-shell-horizontal" d={bodyPath} />
      <ellipse className="tank-endcap" cx={rightCapX} cy={centerY} rx="47" ry="59" />
      <ellipse className="tank-endcap faint" cx={leftCapX} cy={centerY} rx="47" ry="59" />
      <line className="fluid-surface" x1={body.x + 28} y1={fillY} x2={body.x + body.width - 28} y2={fillY} />
      {metrics.showMinimum && metrics.showLimitLines ? <line className="min-line" x1={body.x + 22} y1={minY} x2={body.x + body.width - 22} y2={minY} /> : null}
      {metrics.showRedZone && metrics.showLimitLines ? <line className="red-line" x1={body.x + 22} y1={redY} x2={body.x + body.width - 22} y2={redY} /> : null}
      <path className="tank-saddle" d="M92 194h42l-8 22H100zM296 194h42l-8 22h-26z" />
      <path className="tank-base" d="M82 214h269v10H82z" />
      <path className="tank-cap" d="M203 51h47v19h-47zM196 47h60v6h-60z" />
      <path className="tank-nozzle" d="M363 112h28v18h-28zM391 108h16v26h-16" />
    </svg>
  );
}

function VerticalAxis({ tank, x, inner, fillY, redY, minY, metrics }) {
  const bottom = inner.y + inner.height;

  return (
    <g className="tank-axis">
      {metrics.showCapacityLabel ? (
        <>
          <line x1={x + 30} y1={inner.y} x2={inner.x - 14} y2={inner.y} />
          <text x={x} y={inner.y - 5}>100%</text>
          <text x={x} y={inner.y + 14}>{compactLiters(tank.capacityLiters)}</text>
        </>
      ) : null}

      {metrics.showActualLabel ? (
        <>
          <line className="actual" x1={x + 30} y1={fillY} x2={inner.x - 14} y2={fillY} />
          <path className="actual-arrow" d={`M${inner.x - 14} ${fillY}h25l-10-9v18z`} />
          <text className="actual-text" x={x} y={fillY - 8}>{Math.round(percentFull(tank) * 100)}%</text>
          <text x={x} y={fillY + 14}>{compactLiters(tank.volumeLiters)}</text>
        </>
      ) : null}

      {metrics.showRedZone ? (
        <>
          <line className="red" x1={x + 30} y1={redY} x2={inner.x - 14} y2={redY} />
          <text className="red-text" x={x + 2} y={redY - 9}>Red</text>
        </>
      ) : null}

      {metrics.showMinimum ? (
        <>
          <line className="min" x1={x + 30} y1={minY} x2={inner.x - 14} y2={minY} />
          <text className="min-text" x={x + 74} y={minY + 22}>Min</text>
        </>
      ) : null}

      {metrics.showZeroLabel ? <line x1={x + 30} y1={bottom} x2={inner.x - 14} y2={bottom} /> : null}
    </g>
  );
}

function HorizontalAxis({ tank, x, body, fillY, redY, minY, metrics }) {
  const bottom = body.y + body.height;
  return (
    <g className="tank-axis horizontal-axis">
      {metrics.showCapacityLabel ? (
        <>
          <line x1={x + 45} y1={body.y} x2={body.x + 14} y2={body.y} />
          <text x={x} y={body.y - 6}>100%</text>
          <text x={x} y={body.y + 13}>{compactLiters(tank.capacityLiters)}</text>
        </>
      ) : null}

      {metrics.showActualLabel ? (
        <>
          <line className="actual" x1={x + 45} y1={fillY} x2={body.x + 14} y2={fillY} />
          <text className="actual-text" x={x} y={fillY - 8}>{Math.round(percentFull(tank) * 100)}%</text>
          <text x={x} y={fillY + 13}>{compactLiters(tank.volumeLiters)}</text>
        </>
      ) : null}

      {metrics.showRedZone ? (
        <>
          <line className="red" x1={x + 45} y1={redY} x2={body.x + 14} y2={redY} />
          <text className="red-text" x={x} y={redY - 7}>Red</text>
        </>
      ) : null}

      {metrics.showMinimum ? (
        <>
          <line className="min" x1={x + 45} y1={minY} x2={body.x + 14} y2={minY} />
          <text className="min-text" x={x} y={minY + 14}>Min</text>
        </>
      ) : null}

      {metrics.showZeroLabel ? (
        <>
          <line x1={x + 45} y1={bottom} x2={body.x + 14} y2={bottom} />
          <text x={x} y={bottom + 13}>0 L</text>
        </>
      ) : null}
    </g>
  );
}
